import { useState, useEffect } from "react";

import MetaHead from "components/base/MetaHead";
import Text from "components/base/Text";
import MainContainer from "components/sections/MainContainer";
import FloatingCTA from "components/blocks/FloatingCTA";
import BlogSearchField from "components/blocks/BlogSearchField";
import TagFilter from "components/blog/page/TagFilter";
import LoadingSkeleton from "components/blog/page/LoadingSkeleton";
import NoResults from "components/blog/page/NoResults";
import PostList from "components/blog/page/PostList";
import LoadMoreButton from "components/blog/page/LoadMoreButton";
import { allowedLocales } from "translations";
import Trans from "components/base/Trans";
import { useRouter } from "next/router";
import {
  MotionContainer as Container,
  MotionFlex as Flex,
  staggeredAppear,
  appearBelow,
} from "utils/motion";
import { getAllBlogContent, getSearchedBlogContent } from "lib/payloadApi";

const MIN_SEARCH_LENGTH = 3;

export default function Post({ initialPosts, tags: allAvailableTags }) {
  // Add tags prop
  const [{ allPosts, page, loading }, setPageState] = useState({
    allPosts: initialPosts,
    page: 1,
    loading: false,
  });
  const router = useRouter();
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(""); // New state for debounced search
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedTags, setSelectedTags] = useState([]); // State for selected tags
  const [tagLoading, setTagLoading] = useState(false); // State for tag loading

  // Debounce function
  const debounce = (func, delay) => {
    let timeoutId;
    return function (...args) {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        func.apply(this, args);
      }, delay);
    };
  };

  // Debounced setter for the API call
  const debouncedSetDebouncedSearchQuery = debounce((query) => {
    setDebouncedSearchQuery(query);
  }, 300); // 300ms delay

  // Handler for search input changes - updates searchQuery immediately
  const handleSearchChange = (query) => {
    setSelectedTags([]); // Clear tags when searching
    setSearchQuery(query); // Update the input value immediately
    debouncedSetDebouncedSearchQuery(query); // Trigger the debounced update for API call
  };

  // Effect to trigger API call based on debouncedSearchQuery
  useEffect(() => {
    const selectedLocale =
      allowedLocales.indexOf(router.locale) > -1 ? router.locale : null;
    if (debouncedSearchQuery.trim().length > MIN_SEARCH_LENGTH) {
      setSearchLoading(true);
      setSearchResults([]); // Clear previous results
      setSelectedTags([]); // Ensure tags are cleared

      // Use the new search function with debouncedSearchQuery
      getSearchedBlogContent(selectedLocale, debouncedSearchQuery.trim())
        .then((response) => {
          setSearchResults(response?.results || []);
        })
        .catch((error) => {
          console.error("Failed to fetch search results:", error);
        })
        .finally(() => {
          setSearchLoading(false);
        });
    } else {
      setSearchResults([]);
      setSearchLoading(false);
    }
  }, [debouncedSearchQuery, router.locale]); // Watch debouncedSearchQuery

  // Effect for fetching posts when selectedTags change
  useEffect(() => {
    // Only run if tags are selected and not currently searching
    if (selectedTags.length > 0 && !searchQuery) {
      setTagLoading(true);
      setPageState((prev) => ({ ...prev, page: 1, allPosts: null })); // Reset posts and page
      const selectedLocale =
        allowedLocales.indexOf(router.locale) > -1 ? router.locale : null;

      getAllBlogContent(selectedLocale, 1, 20, selectedTags) // Fetch page 1 with tags
        .then((newPage) => {
          setPageState({ allPosts: newPage, page: 1, loading: false });
        })
        .catch((error) => {
          console.error("Failed to fetch posts by tag:", error);
          setPageState({ allPosts: null, page: 1, loading: false }); // Reset on error
        })
        .finally(() => {
          setTagLoading(false);
        });
    } else if (selectedTags.length === 0 && !searchQuery) {
      // If tags are cleared and not searching, fetch initial unfiltered posts
      setTagLoading(true);
      fetchInitialPosts(); // Reuse initial fetch logic
      setTagLoading(false);
    }
  }, [selectedTags, router.locale]); // Rerun when tags or locale change

  const loadMore = () => {
    // Disable load more when searching or tag loading
    if (searchQuery || tagLoading) return;
    setPageState((prev) => ({ ...prev, page: prev.page + 1, loading: true }));
  };

  useEffect(() => {
    const selectedLocale =
      allowedLocales.indexOf(router.locale) > -1 ? router.locale : null;
    // Fetch initial page using the new function
    getAllBlogContent(selectedLocale, 1).then((newPage) => {
      // Only update if not searching and no tags selected initially
      if (!searchQuery && selectedTags.length === 0) {
        setPageState({ allPosts: newPage, page: 1, loading: false });
      }
    });
  }, [router.locale]); // Removed selectedTags dependency here to avoid loop

  // Helper function for initial fetch
  const fetchInitialPosts = () => {
    const selectedLocale =
      allowedLocales.indexOf(router.locale) > -1 ? router.locale : null;
    getAllBlogContent(selectedLocale, 1).then((newPage) => {
      setPageState({ allPosts: newPage, page: 1, loading: false });
    });
  };

  // Effect for loading more posts (handles pagination for both tags and no filter)
  useEffect(() => {
    if (loading && !searchQuery && !tagLoading) {
      const selectedLocale =
        allowedLocales.indexOf(router.locale) > -1 ? router.locale : null;
      // Pass selectedTags to pagination fetch
      getAllBlogContent(selectedLocale, page, 20, selectedTags)
        .then((newPage) => {
          const currentResults = allPosts?.results || [];
          const newPageResults = newPage?.results || [];
          const combinedResults = currentResults.concat(newPageResults);

          const newPostsObject = {
            ...allPosts, // Spread existing properties
            ...newPage, // Spread new page properties (like total_pages)
            results: combinedResults, // Set combined results
            page, // Update page number
          };

          setPageState({ allPosts: newPostsObject, page, loading: false });
        })
        .catch((error) => {
          console.error("Failed to load more posts:", error);
          setPageState((prev) => ({ ...prev, loading: false })); // Stop loading on error
        });
    } else if (loading) {
      // If loading was triggered inappropriately (e.g., during search/tag load), turn it off
      setPageState((prev) => ({ ...prev, loading: false }));
    }
  }, [
    loading,
    page,
    searchQuery,
    tagLoading,
    selectedTags,
    router.locale,
    allPosts,
  ]); // Add dependencies

  // Handler for clicking a tag
  const handleTagClick = (tag) => {
    setSearchQuery(""); // Clear search query (for immediate input clear)
    setDebouncedSearchQuery(""); // Clear debounced search query (to stop pending API calls)
    setSearchResults([]); // Clear search results
    setSearchLoading(false);

    setSelectedTags((prevTags) => {
      if (prevTags.includes(tag)) {
        // If tag is already selected, remove it
        return prevTags.filter((t) => t !== tag);
      } else {
        // Otherwise, add it
        return [...prevTags, tag];
      }
    });
  };

  // Determine which posts to display based on search or tags
  const displayPosts =
    searchQuery.trim().length > MIN_SEARCH_LENGTH
      ? searchResults
      : allPosts?.results || [];
  const showFeaturedPost =
    searchQuery.trim().length <= MIN_SEARCH_LENGTH &&
    selectedTags.length === 0 &&
    displayPosts.length > 0;
  const showLoadMore =
    !searchQuery && !tagLoading && allPosts?.total_pages > allPosts?.page;

  return (
    <MainContainer
      navStyle={{ gradient: false, inverted: true }}
      bg="white"
    >
      <MetaHead
        title="LBL_BLOG_META_TITLE"
        keywords={["LBL_META_KEYWORD_2"]}
        description="LBL_BLOG_META_DESC"
        imageUrl="/images/meta/blog.jpg"
      />
      {(initialPosts?.results?.length > 0 ||
        searchQuery ||
        searchLoading ||
        tagLoading ||
        selectedTags.length > 0) && (
        <Container
          initial="hidden"
          animate="visible"
          bg="white"
          variants={staggeredAppear}
          maxWidth="xl"
          pt={[32, null, 40]}
          pb={[16, null, 32]}
          px={[6, null, 8]}
        >
          <Flex
            justifyContent="space-between"
            mb={[6, null, 10]}
            width="100%"
            direction={["column", null, null, "row"]}
            gap={4}
          >
            <Text type="headline_md" variants={appearBelow} color="black.700">
              <Trans id="LBL_BLOG_TITLE" />
            </Text>
            <BlogSearchField
              onChange={handleSearchChange}
              loading={searchLoading}
              value={searchQuery}
            />
          </Flex>

          <TagFilter
            allAvailableTags={allAvailableTags}
            selectedTags={selectedTags}
            onTagClick={handleTagClick}
            isLoading={tagLoading || searchLoading}
          />
          {/* Conditionally render Featured Post */}
          {(searchLoading || tagLoading) && <LoadingSkeleton />}

          {!searchLoading &&
            searchQuery &&
            searchQuery.length >= MIN_SEARCH_LENGTH &&
            displayPosts.length === 0 && (
              <NoResults messageId="LBL_BLOG_SEARCH_NO_RESULTS" />
            )}

          {!tagLoading &&
            selectedTags.length > 0 &&
            displayPosts.length === 0 && (
              <NoResults messageId="LBL_BLOG_TAGS_NO_RESULTS" bgColor="red" />
            )}

          {!searchLoading && !tagLoading && displayPosts.length > 0 && (
            <PostList
              postsToDisplay={displayPosts}
              showFeaturedPostCondition={showFeaturedPost}
              searchQuery={searchQuery}
              selectedTags={selectedTags}
            />
          )}

          {showLoadMore && (
            <LoadMoreButton
              onClick={loadMore}
              isLoading={loading && !searchQuery && !tagLoading}
            />
          )}
        </Container>
      )}
      <FloatingCTA />
    </MainContainer>
  );
}

export async function getStaticProps(ctx) {
  const { getAllBlogContent, getAllTags } = await import("lib/payloadApi");
  const selectedLocale =
    allowedLocales.indexOf(ctx.locale) > -1 ? ctx.locale : null;

  const allPosts = await getAllBlogContent(selectedLocale, 1);
  const tags = await getAllTags(selectedLocale, "blog");
  return {
    props: { initialPosts: allPosts, tags },
    revalidate: 60,
  };
}
